import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  createOwnerTrackerCase,
  listOwnerTrackerCases,
  updateOwnerTrackerCase,
} from "../../../api/adminTrackerApi";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function safe(v) {
  return v == null ? "" : String(v);
}

function fmtDateTime(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function useDebouncedValue(value, delayMs = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replaceAll('"', '""')}"`;
  return s;
}

function downloadCsv(filename, rows) {
  const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function Chip({ tone = "slate", children, compact = false }) {
  const cls =
    tone === "green"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : tone === "red"
      ? "bg-rose-50 text-rose-800 border-rose-200"
      : tone === "amber"
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : tone === "blue"
      ? "bg-sky-50 text-sky-900 border-sky-200"
      : "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-md border font-semibold",
        compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]",
        cls
      )}
    >
      {children}
    </span>
  );
}

function KpiCard({ title, value, hint, tone = "slate" }) {
  const ring =
    tone === "green"
      ? "ring-emerald-100"
      : tone === "red"
      ? "ring-rose-100"
      : tone === "amber"
      ? "ring-amber-100"
      : tone === "blue"
      ? "ring-sky-100"
      : "ring-slate-100";

  return (
    <div className={classNames("rounded-xl border border-slate-200 bg-white px-4 py-3 ring-4", ring)}>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{title}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      {hint ? <div className="mt-1 text-xs text-slate-500">{hint}</div> : null}
    </div>
  );
}

function statusTone(v) {
  const x = safe(v).toUpperCase();
  if (x === "VERIFIED" || x === "CLOSED") return "green";
  if (x === "NOT_VERIFIED" || x === "BLOCKED") return "red";
  if (x === "FOLLOW_UP" || x === "NO_CAR") return "amber";
  if (x === "PENDING") return "blue";
  return "slate";
}

function complianceTone(v) {
  const x = safe(v).toUpperCase();
  if (x === "APPROVED_VERIFIED") return "green";
  if (x === "ACTION_REQUIRED") return "red";
  if (x === "PENDING_ADMIN_REVIEW") return "amber";
  if (x === "SUBMITTED") return "blue";
  return "slate";
}

function docStateTone(v) {
  const x = safe(v).toUpperCase();
  if (x === "VERIFIED") return "green";
  if (x === "ACTION_REQUIRED") return "red";
  if (x === "PENDING_REVIEW") return "amber";
  if (x === "UPLOADED") return "blue";
  return "slate";
}

function labelStatus(v) {
  return safe(v).replaceAll("_", " ") || "-";
}

function rowKey(row) {
  return `${row.phoneNumber || ""}|${row.vehicleNumber || ""}`;
}

function documentStatusesText(row) {
  const items = Array.isArray(row?.documentStatuses) ? row.documentStatuses : [];
  if (!items.length) return "";
  return items
    .map((item) => {
      const required = item?.required === false ? "optional" : "required";
      const detail = item?.issueMessage ? ` (${item.issueMessage})` : "";
      return `${item?.label || item?.docType || "Document"}: ${labelStatus(item?.state)} [${required}]${detail}`;
    })
    .join(" | ");
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path d="M13.586 3.586a2 2 0 112.828 2.828l-8.25 8.25a2 2 0 01-.878.513l-2.836.81a.75.75 0 01-.927-.927l.81-2.836a2 2 0 01.513-.878l8.25-8.25z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3.25-3.25a1 1 0 111.414-1.414l2.543 2.543 6.543-6.543a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

export default function AdminOwnerTrackerPage() {
  const [q, setQ] = useState("");
  const qDebounced = useDebouncedValue(q, 350);
  const [status, setStatus] = useState("ALL");
  const [complianceStatus, setComplianceStatus] = useState("ALL");
  const [vehiclePresence, setVehiclePresence] = useState("ALL");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  const [editingCell, setEditingCell] = useState(null);
  const [cellDraft, setCellDraft] = useState("");
  const [saveError, setSaveError] = useState("");
  const [savingCell, setSavingCell] = useState(false);

  const fetchSeq = useRef(0);

  useEffect(() => {
    setPage(0);
  }, [qDebounced, status, complianceStatus, vehiclePresence, size]);

  const load = useCallback(async () => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    setError("");
    try {
      const resp = await listOwnerTrackerCases({
        q: qDebounced,
        status: status === "ALL" ? undefined : status,
        complianceStatus: complianceStatus === "ALL" ? undefined : complianceStatus,
        vehiclePresence: vehiclePresence === "ALL" ? undefined : vehiclePresence,
        page,
        size,
      });
      if (seq !== fetchSeq.current) return;
      setData(resp || null);
    } catch (e) {
      if (seq !== fetchSeq.current) return;
      setError(e?.message || "Failed to load owner tracker cases");
      setData(null);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [complianceStatus, page, qDebounced, size, status, vehiclePresence]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.content || [];
  const totalPages = Number(data?.totalPages || 0);
  const summary = data?.summary;
  const summaryCards = [
    { title: "Total Owners", value: Number(summary?.totalOwners || 0).toLocaleString("en-IN"), hint: "From database", tone: "blue" },
    { title: "Total Vehicles", value: Number(summary?.totalVehicles || 0).toLocaleString("en-IN"), hint: "From database", tone: "blue" },
    { title: "Verified Vehicles", value: Number(summary?.verifiedVehicles || 0).toLocaleString("en-IN"), hint: "APPROVED_VERIFIED", tone: "green" },
    { title: "Not Verified", value: Number(summary?.notVerifiedVehicles || 0).toLocaleString("en-IN"), hint: "Vehicle DB total - verified", tone: "amber" },
    { title: "No Docs", value: Number(summary?.noDocsVehicles || 0).toLocaleString("en-IN"), hint: "Vehicles without uploaded docs", tone: "red" },
    { title: "No Cars", value: Number(summary?.noCarsOwners || 0).toLocaleString("en-IN"), hint: "Owners without any vehicle", tone: "amber" },
    { title: "Listed Rows", value: Number(summary?.trackedCases || 0).toLocaleString("en-IN"), hint: "Auto-generated live tracker rows", tone: "blue" },
    { title: "Rows With Vehicle", value: Number(summary?.trackedCasesWithVehicle || 0).toLocaleString("en-IN"), hint: "Rows linked to a reg no", tone: "green" },
  ];

  function beginEditCell(row, field) {
    setEditingCell({ rowKey: rowKey(row), field });
    setCellDraft(safe(row?.[field]));
    setSaveError("");
  }

  function cancelEditCell(force = false) {
    if (savingCell && !force) return;
    setEditingCell(null);
    setCellDraft("");
    setSaveError("");
  }

  function buildRowPayload(row, overrides = {}) {
    return {
      id: row?.id || null,
      phoneNumber: row?.phoneNumber || "",
      name: row?.name || row?.ownerNameFromDb || "",
      vehicleNumber: row?.vehicleNumber || "",
      trackerStatus: row?.trackerStatus || "NOT_VERIFIED",
      reason: row?.reason || "",
      comment: row?.comment || "",
      callStatus: row?.callStatus || "",
      ...overrides,
    };
  }

  async function saveCell(row, field) {
    setSavingCell(true);
    setSaveError("");
    try {
      const payload = buildRowPayload(row, { [field]: cellDraft });
      if (payload.id) {
        await updateOwnerTrackerCase(payload.id, payload);
      } else {
        await createOwnerTrackerCase(payload);
      }
      cancelEditCell(true);
      await load();
    } catch (e) {
      setSaveError(e?.message || "Failed to save owner tracker field");
    } finally {
      setSavingCell(false);
    }
  }

  async function exportCsv() {
    try {
      const resp = await listOwnerTrackerCases({
        q: qDebounced,
        status: status === "ALL" ? undefined : status,
        complianceStatus: complianceStatus === "ALL" ? undefined : complianceStatus,
        vehiclePresence: vehiclePresence === "ALL" ? undefined : vehiclePresence,
        page: 0,
        size: 500,
      });
      const items = resp?.content || [];
      const lines = [
        [
          "Phone Number",
          "Name",
          "Vehicle Number",
          "Tracker Status",
          "Reason",
          "Comment",
          "Call Status",
          "Owner Id",
          "DB Owner Name",
          "Vehicle Exists",
          "Compliance Status",
          "Compliance Complete",
          "Compliance Verified",
          "Uploaded Docs",
          "Required Docs",
          "Uploaded Required Docs",
          "Verified Required Docs",
          "Missing Required Docs",
          "Document Summary",
          "Document Statuses",
          "Uploaded Photos",
          "App User Match",
          "Driver Match",
          "Updated At",
        ].map(csvEscape).join(","),
      ];

      items.forEach((row) => {
        lines.push(
          [
            row.phoneNumber,
            row.name,
            row.vehicleNumber,
            row.trackerStatus,
            row.reason,
            row.comment,
            row.callStatus,
            row.ownerId,
            row.ownerNameFromDb,
            row.vehicleExists,
            row.complianceStatus,
            row.complianceComplete,
            row.complianceVerified,
            row.uploadedDocsCount,
            row.requiredDocsCount,
            row.uploadedRequiredDocsCount,
            row.verifiedRequiredDocsCount,
            row.missingRequiredDocsCount,
            row.documentStatusSummary,
            documentStatusesText(row),
            row.uploadedPhotosCount,
            row.appUserMatch,
            row.driverMatch,
            row.updatedAt,
          ].map(csvEscape).join(",")
        );
      });

      const stamp = new Date().toISOString().slice(0, 19).replaceAll(":", "-");
      downloadCsv(`owner-tracker-${stamp}.csv`, lines);
    } catch (e) {
      window.alert(e?.message || "Failed to export owner tracker");
    }
  }

  function renderEditableCell(row, field, options) {
    const active = editingCell?.rowKey === rowKey(row) && editingCell?.field === field;
    const display = options.renderDisplay ? options.renderDisplay(row) : safe(row?.[field]) || "-";

    if (active) {
      return (
        <div className="flex min-w-0 items-start gap-2">
          {options.type === "select" ? (
            <select
              value={cellDraft}
              onChange={(e) => setCellDraft(e.target.value)}
              className="h-9 min-w-[150px] rounded-md border border-slate-200 px-3 text-sm text-slate-900"
            >
              {options.choices.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              value={cellDraft}
              onChange={(e) => setCellDraft(e.target.value)}
              className={classNames(
                "h-9 rounded-md border border-slate-200 px-3 text-sm text-slate-900",
                options.inputClassName || "w-[220px]"
              )}
            />
          )}

          <div className="flex items-center gap-1">
            <button
              onClick={() => saveCell(row, field)}
              disabled={savingCell}
              className="rounded-md bg-slate-900 p-2 text-white hover:bg-slate-800 disabled:opacity-50"
              aria-label={`Save ${field}`}
              title="Save"
            >
              <CheckIcon />
            </button>
            <button
              onClick={cancelEditCell}
              disabled={savingCell}
              className="rounded-md border border-slate-200 bg-white p-2 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              aria-label={`Cancel ${field}`}
              title="Cancel"
            >
              <XIcon />
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex min-w-0 items-start gap-2">
        <div className="min-w-0 flex-1">{display}</div>
        <button
          onClick={() => beginEditCell(row, field)}
          className="mt-0.5 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          aria-label={`Edit ${field}`}
          title="Edit"
        >
          <PencilIcon />
        </button>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-6 py-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Owner Cases</div>
              <div className="mt-1 text-sm text-slate-600">
                Full owner list from the live owner and vehicle database, with direct field edits and per-document vehicle compliance visibility.
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Chip tone="blue">DIRECT EDIT</Chip>
              <Chip tone="green">DOC STATUS LIVE</Chip>
              <Chip>EXPORTABLE</Chip>
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 2xl:grid-cols-4">
            {summaryCards.map((item) => (
              <KpiCard key={item.title} {...item} />
            ))}
          </div>
        </div>

        <div className="border-b border-slate-200 bg-slate-50/50 px-6 py-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
            <div className="grid gap-3 md:grid-cols-2 2xl:flex 2xl:flex-wrap 2xl:items-end">
              <label className="block w-full 2xl:min-w-[280px] 2xl:flex-1">
                <div className="text-xs font-semibold text-slate-600">Search</div>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Phone / name / vehicle / doc status / comment"
                  className="mt-2 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900"
                />
              </label>

              <label className="block">
                <div className="text-xs font-semibold text-slate-600">Tracker status</div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900">
                  <option value="ALL">All</option>
                  <option value="VERIFIED">Verified</option>
                  <option value="NOT_VERIFIED">Not verified</option>
                  <option value="FOLLOW_UP">Follow up</option>
                  <option value="PENDING">Pending</option>
                  <option value="CLOSED">Closed</option>
                  <option value="NO_CAR">No car</option>
                </select>
              </label>

              <label className="block">
                <div className="text-xs font-semibold text-slate-600">Compliance</div>
                <select value={complianceStatus} onChange={(e) => setComplianceStatus(e.target.value)} className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900">
                  <option value="ALL">All</option>
                  <option value="APPROVED_VERIFIED">Approved / Verified</option>
                  <option value="PENDING_ADMIN_REVIEW">Pending review</option>
                  <option value="ACTION_REQUIRED">Action required</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="DRAFT">Draft</option>
                  <option value="NO_CAR">No car linked</option>
                </select>
              </label>

              <label className="block">
                <div className="text-xs font-semibold text-slate-600">Vehicle presence</div>
                <select value={vehiclePresence} onChange={(e) => setVehiclePresence(e.target.value)} className="mt-2 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900">
                  <option value="ALL">All</option>
                  <option value="HAS_CAR">Has car</option>
                  <option value="NO_CAR">No car</option>
                </select>
              </label>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between xl:justify-end">
              <button onClick={exportCsv} className="rounded-md border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50">
                Export CSV
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[2300px] w-full">
            <thead className="bg-white">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-6 py-3">Phone</th>
                <th className="px-6 py-3">Name</th>
                <th className="px-6 py-3">Vehicle</th>
                <th className="px-6 py-3">Tracker status</th>
                <th className="px-6 py-3">Reason</th>
                <th className="px-6 py-3">Comment</th>
                <th className="px-6 py-3">Call status</th>
                <th className="px-6 py-3">Owner</th>
                <th className="px-6 py-3">Vehicle found</th>
                <th className="px-6 py-3">Compliance</th>
                <th className="px-6 py-3">Documents</th>
                <th className="px-6 py-3 text-right">Photos</th>
                <th className="px-6 py-3">App user</th>
                <th className="px-6 py-3">Driver</th>
                <th className="px-6 py-3">Updated</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200">
              {loading ? (
                Array.from({ length: 8 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    {Array.from({ length: 15 }).map((__, col) => (
                      <td key={col} className="px-6 py-4">
                        <div className="h-4 rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : error ? (
                <tr>
                  <td colSpan={15} className="px-6 py-8">
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={15} className="px-6 py-10 text-sm text-slate-600">No owner tracker rows found.</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={rowKey(row)} className="align-top hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm font-semibold text-slate-900 whitespace-nowrap">{row.phoneNumber || "-"}</td>
                    <td className="px-6 py-4 text-sm text-slate-700">
                      {renderEditableCell(row, "name", { inputClassName: "w-[180px]" })}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap">{row.vehicleNumber || "-"}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderEditableCell(row, "trackerStatus", {
                        type: "select",
                        choices: [
                          { value: "VERIFIED", label: "Verified" },
                          { value: "NOT_VERIFIED", label: "Not verified" },
                          { value: "FOLLOW_UP", label: "Follow up" },
                          { value: "PENDING", label: "Pending" },
                          { value: "CLOSED", label: "Closed" },
                          { value: "NO_CAR", label: "No car" },
                        ],
                        renderDisplay: (item) => <Chip tone={statusTone(item.trackerStatus)}>{labelStatus(item.trackerStatus)}</Chip>,
                      })}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700">
                      {renderEditableCell(row, "reason", { inputClassName: "w-[230px]" })}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700">
                      {renderEditableCell(row, "comment", { inputClassName: "w-[250px]" })}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700">
                      {renderEditableCell(row, "callStatus", { inputClassName: "w-[220px]" })}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap">
                      {row.ownerId ? (
                        <NavLink to={`/admin/support/owners/${encodeURIComponent(String(row.ownerId))}/overview`} className="font-semibold text-sky-700 hover:text-sky-800">
                          {row.ownerNameFromDb || `Owner ${row.ownerId}`}
                        </NavLink>
                      ) : (
                        row.ownerNameFromDb || "-"
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Chip tone={row.vehicleExists ? "green" : "red"}>{row.vehicleExists ? "Yes" : "No"}</Chip>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-2">
                        <Chip tone={complianceTone(row.complianceStatus)}>{labelStatus(row.complianceStatus)}</Chip>
                        <div className="text-xs text-slate-500">
                          {row.complianceComplete ? "Complete" : "Incomplete"} / {row.complianceVerified ? "Verified" : "Not verified"}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-700 min-w-[430px]">
                      <div className="space-y-2">
                        <div className="font-medium text-slate-900">{row.documentStatusSummary || (row.vehicleExists ? "No document status" : "No car added")}</div>
                        {row.missingRequiredDocsCount > 0 ? (
                          <div className="text-xs text-rose-700">
                            Missing: {Array.isArray(row.missingDocumentTypes) && row.missingDocumentTypes.length ? row.missingDocumentTypes.join(", ") : `${row.missingRequiredDocsCount} document(s)`}
                          </div>
                        ) : null}
                        <div className="flex flex-wrap gap-1.5">
                          {(row.documentStatuses || []).map((doc) => (
                            <Chip key={`${rowKey(row)}-${doc.docType || doc.label}`} tone={docStateTone(doc.state)} compact>
                              {doc.label}: {labelStatus(doc.state)}
                              {doc.required === false ? " (opt)" : ""}
                            </Chip>
                          ))}
                        </div>
                        {(row.documentStatuses || []).some((doc) => doc.issueMessage) ? (
                          <div className="space-y-1">
                            {(row.documentStatuses || [])
                              .filter((doc) => doc.issueMessage)
                              .map((doc) => (
                                <div key={`${rowKey(row)}-${doc.docType || doc.label}-issue`} className="text-xs text-slate-500">
                                  <span className="font-semibold text-slate-700">{doc.label}:</span> {doc.issueMessage}
                                </div>
                              ))}
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-slate-700 whitespace-nowrap">{row.uploadedPhotosCount ?? 0}</td>
                    <td className="px-6 py-4 whitespace-nowrap"><Chip tone={row.appUserMatch ? "green" : "slate"}>{row.appUserMatch ? "Matched" : "No"}</Chip></td>
                    <td className="px-6 py-4 whitespace-nowrap"><Chip tone={row.driverMatch ? "green" : "slate"}>{row.driverMatch ? "Matched" : "No"}</Chip></td>
                    <td className="px-6 py-4 text-sm text-slate-700 whitespace-nowrap">{fmtDateTime(row.updatedAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {saveError ? (
          <div className="border-t border-rose-200 bg-rose-50 px-6 py-3 text-sm text-rose-800">{saveError}</div>
        ) : null}

        <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-6 py-4 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-slate-500">
            Showing <span className="font-semibold text-slate-700">{rows.length}</span> of{" "}
            <span className="font-semibold text-slate-700">{Number(data?.totalElements || 0).toLocaleString("en-IN")}</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <select value={size} onChange={(e) => setSize(Number(e.target.value))} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900">
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n} / page</option>
              ))}
            </select>

            <div className="text-sm text-slate-700">
              Page <span className="font-semibold">{page + 1}</span> of <span className="font-semibold">{Math.max(1, totalPages)}</span>
            </div>

            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page <= 0} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50">
              Prev
            </button>
            <button onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))} disabled={page + 1 >= totalPages} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
